import React, { FC } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import IntakeFormConfirmInfo from "./intakeFormConfirmInfo";
import {
  IntakeStepClinicInfo,
  IntakeStepSelectTest,
  IntakeStepActiveDisease,
  IntakeStepCancerType,
  IntakeStepCancerStage,
  IntakeStepReason,
  IntakeStepUnderstanding,
  IntakeStepPreference,
  IntakeStepDoctorAppointment,
  IntakeStepLocationInterpreter,
  IntakeStepConsent,
} from "./steps";

/**
 * Router component for the GIS intake form flow.
 * IntakeFormProvider (via OncUppGisTriageLayout) is mounted at the layout-route
 * level in routing.tsx so user-input state persists across intake → patient-education
 * → schedule, scoped per order + test.
 *
 * Flow:
 * Step 1: /intake - Confirm contact info (phone/email)
 * Step 2: /intake/active-disease - Active disease or remission
 * Step 3: /intake/reason - Reason for appointment
 * Step 4: /intake/doctor-appointment - Doctor appointment scheduled
 * Step 5: /intake/location-interpreter - Location & interpreter preference
 * Step 6: /intake/understanding - Ease of understanding information
 * Step 7: /intake/preference - Read first or talk first
 * Step 8: /intake/consent - Review and confirm consent (submits form and navigates to patient education)
 *
 * No-order ("no test found") flow inserts four extra steps that collect data
 * a backing order would otherwise pre-fill:
 * - /intake/clinic-info - Ordering physician and clinic
 * - /intake/select-test - Which test the session is for
 * - /intake/cancer-type - Type of cancer
 * - /intake/cancer-stage - Cancer stage
 */
const IntakeFormRouter: FC = () => {
  return (
    <Routes>
      {/* Step 1: Confirm contact info */}
      <Route path="/" element={<IntakeFormConfirmInfo />} />

      {/* No-order: ordering physician and clinic */}
      <Route path="/clinic-info" element={<IntakeStepClinicInfo />} />

      {/* No-order: select which test */}
      <Route path="/select-test" element={<IntakeStepSelectTest />} />

      {/* Step 2: Active disease or remission */}
      <Route path="/active-disease" element={<IntakeStepActiveDisease />} />

      {/* No-order: cancer type */}
      <Route path="/cancer-type" element={<IntakeStepCancerType />} />

      {/* No-order: cancer stage */}
      <Route path="/cancer-stage" element={<IntakeStepCancerStage />} />

      {/* Step 3: Reason for appointment */}
      <Route path="/reason" element={<IntakeStepReason />} />

      {/* Step 4: Doctor appointment scheduled */}
      <Route
        path="/doctor-appointment"
        element={<IntakeStepDoctorAppointment />}
      />

      {/* Step 5: Location & interpreter preference */}
      <Route
        path="/location-interpreter"
        element={<IntakeStepLocationInterpreter />}
      />

      {/* Step 6: Ease of understanding */}
      <Route path="/understanding" element={<IntakeStepUnderstanding />} />

      {/* Step 7: Preference - read first or talk */}
      <Route path="/preference" element={<IntakeStepPreference />} />

      {/* Step 8: Consent confirmation */}
      <Route path="/consent" element={<IntakeStepConsent />} />

      {/* Fallback: redirect to first step */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default IntakeFormRouter;
